import React, { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useTranslations } from 'hooks/useTranslations';
import { copyToClipboard } from 'utils/clipboard';
import { TruncatedText } from '../../atoms/TruncatedText';
import { EAlertTime } from '../../../utils/date';

export interface CopyableTruncatedFieldProps {
  label?: string;
  value?: string | null;
  maxLength?: number;
  buttonLabel?: string;
  buttonIcon?: EIconTypes;
  buttonVariant?: EButtonVariants;
  buttonSize?: EButtonSizes;
  buttonSizePX?: EButtonSizesPX;
  isButtonFullWidth?: boolean;
}

export const CopyableTruncatedField: React.FC<CopyableTruncatedFieldProps> = ({
  label,
  value,
  maxLength = 40,
  buttonLabel,
  buttonIcon = EIconTypes.copy,
  buttonVariant = EButtonVariants.outlined,
  buttonSize = EButtonSizes.small,
  buttonSizePX = EButtonSizesPX.px40,
  isButtonFullWidth = false,
}) => {
  const { t } = useTranslations();
  const [localAlert, setLocalAlert] = useState<{
    text: string;
    variant: EAlertVariants;
  } | null>(null);

  const handleCopy = async (val: string | null | undefined) => {
    if (!val) return;
    try {
      await copyToClipboard(val);
      setLocalAlert({
        text: t('general.value.copied.success.text'),
        variant: EAlertVariants.success,
      });

      setTimeout(() => {
        setLocalAlert(null);
      }, EAlertTime.SHORT);
    } catch (e: any) {
      setLocalAlert({
        text: t('general.copy.error.text'),
        variant: EAlertVariants.error,
      });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}

      {localAlert && (
        <Box sx={{ mb: 1.5 }}>
          <Alert text={localAlert.text} variant={localAlert.variant} />
        </Box>
      )}

      <Grid
        container
        spacing={2}
        sx={{
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
        }}
      >
        <Grid size={{ xs: 'grow' }}>
          <TruncatedText value={value} maxLength={maxLength} />
        </Grid>
        <Grid size={{ xs: 'auto' }}>
          <Button
            label={buttonLabel ?? t('general.copy.button')}
            icon={buttonIcon}
            variant={buttonVariant}
            onClick={() => handleCopy(value)}
            size={buttonSize}
            sizePX={buttonSizePX}
            fullWidth={isButtonFullWidth}
            disabled={!value}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
